<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('attendance.edit_attendance_adjustment') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form ref="formRef" :loading="isLoading" @submit-valid="onSave">
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="editAttendanceAdjustment.attendanceId"
              :label="$t('general.id')"
              disabled
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="editAttendanceAdjustment.systemInOut"
              :label="$t('general.punch_in_out')"
              disabled
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="editAttendanceAdjustment.note"
              :rules="rules.note"
              :label="$t('general.note')"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <timezone-autocomplete
              v-model="editAttendanceAdjustment.punchInTimezoneName"
              :rules="rules.punchInTimezone"
              :label="
                $t('attendance.attendance_adjustment_punchInTimezoneName')
              "
              disabled
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="editAttendanceAdjustment.punchInUserDate"
              type="date"
              :rules="rules.punchInUserDate"
              :label="$t('attendance.attendance_adjustment_punchInUserDate')"
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="editAttendanceAdjustment.punchInUserTime"
              type="time"
              :rules="rules.punchInUserTime"
              :label="$t('attendance.attendance_adjustment_punchInUserTime')"
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <timezone-autocomplete
              v-model="editAttendanceAdjustment.punchOutTimezoneName"
              :rules="rules.punchOutTimezone"
              :label="
                $t('attendance.attendance_adjustment_punchOutTimezoneName')
              "
              disabled
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="editAttendanceAdjustment.punchOutUserDate"
              type="date"
              :rules="rules.punchOutUserDate"
              :label="$t('attendance.attendance_adjustment_punchOutUserDate')"
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="editAttendanceAdjustment.punchOutUserTime"
              type="time"
              :rules="rules.punchOutUserTime"
              :label="$t('attendance.attendance_adjustment_punchOutUserTime')"
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
        </oxd-grid>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
    <confirmation-dialog
      ref="pendingAdjustmentDialog"
      :title="$t('attendance.pending_adjustment_dialog_title')"
      :subtitle="$t('attendance.pending_adjustment_dialog_subtitle')"
      :cancel-label="$t('general.no_cancel')"
      :confirm-label="$t('general.confirm')"
      icon="info-circle"
      confirm-button-type="secondary"
    />
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {
  formatDate,
  formatTime,
  parseDate,
  parseTime,
} from '@ohrm/core/util/helper/datefns';
import {APIService} from '@ohrm/core/util/services/api.service';
import moment from 'moment-timezone';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';
import TimezoneAutocomplete from '../components/TimezoneAutocomplete.vue';
import {
  required,
  shouldNotExceedCharLength,
  validSelection,
  mustNotContainDisallowedNoteLiterals,
} from '@/core/util/validation/rules';
import ConfirmationDialog from '@ohrm/components/dialogs/ConfirmationDialog';
import {isDuplicateAdjustmentSubmissionError} from '@/orangehrmAttendancePlugin/util/adjustmentSubmissionErrors';
import {
  hasCompletePunchPair,
  validatePunchDateTimePair,
} from '@/orangehrmAttendancePlugin/util/punchFieldValidation';
const PENDING_SUBMISSION_STATUS = 1;
const editAttendanceAdjustmentModel = {
  attendanceId: 0,
  submissionId: 0,
  note: '',
  punchInTimezoneName: {
    id: '',
    label: '',
  },
  punchInUserTime: '',
  punchOutTimezoneName: {
    id: '',
    label: '',
  },
  punchInUserDate: '',
  punchOutUserDate: '',
  punchInUserTimeOriginal: '',
  punchOutUserTimeOriginal: '',
  punchOutUserTime: '',
  submissionStatus: 0,
  systemInOut: '',
};
export default {
  components: {
    'timezone-autocomplete': TimezoneAutocomplete,
    'confirmation-dialog': ConfirmationDialog,
  },
  props: {
    attendanceId: {
      type: Number,
      required: true,
    },
    notesDisallowedLiterals: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const {locale} = useLocale();
    const {userDateFormat, timeFormat} = useDateFormat();
    const httpGet = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/my/attendance/records',
    );
    const httpPut = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/adjust',
    );
    return {
      httpGet,
      httpPut,
      locale,
      userDateFormat,
      timeFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      editAttendanceAdjustment: {...editAttendanceAdjustmentModel},
    };
  },
  computed: {
    rules() {
      const requireWhen = (shouldApply) => (shouldApply ? [required] : []);
      const punchInPairRule = () =>
        validatePunchDateTimePair(
          this.editAttendanceAdjustment.punchInUserDate,
          this.editAttendanceAdjustment.punchInUserTime,
        );
      const punchOutPairRule = () =>
        validatePunchDateTimePair(
          this.editAttendanceAdjustment.punchOutUserDate,
          this.editAttendanceAdjustment.punchOutUserTime,
        );

      return {
        note: [
          required,
          shouldNotExceedCharLength(250),
          mustNotContainDisallowedNoteLiterals(this.notesDisallowedLiterals),
        ],
        punchInUserDate: [
          ...requireWhen(this.requiresPunchInFields),
          punchInPairRule,
        ],
        punchOutUserDate: [
          ...requireWhen(this.requiresPunchOutFields),
          punchOutPairRule,
        ],
        punchInUserTime: [
          ...requireWhen(this.requiresPunchInFields),
          punchInPairRule,
        ],
        punchOutUserTime: [
          ...requireWhen(this.requiresPunchOutFields),
          punchOutPairRule,
        ],
        punchInTimezone: this.requiresPunchInFields
          ? [required, validSelection]
          : [],
        punchOutTimezone: this.requiresPunchOutFields
          ? [required, validSelection]
          : [],
      };
    },
    requiresPunchInFields() {
      return !this.hasCompletePunchOut;
    },
    requiresPunchOutFields() {
      return !this.hasCompletePunchIn;
    },
    hasCompletePunchIn() {
      const adjustment = this.editAttendanceAdjustment;
      return hasCompletePunchPair(
        adjustment.punchInUserDate,
        adjustment.punchInUserTime,
      );
    },
    hasCompletePunchOut() {
      const adjustment = this.editAttendanceAdjustment;
      return hasCompletePunchPair(
        adjustment.punchOutUserDate,
        adjustment.punchOutUserTime,
      );
    },
  },
  created() {
    this.isLoading = true;
    this.httpGet
      .get(this.attendanceId)
      .then((response) => {
        this.editAttendanceAdjustment = this.buildEditAttendanceAdjustment(
          response.data.data,
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    revalidateForm() {
      this.$nextTick(() => {
        this.$refs.formRef?.validate?.();
      });
    },
    formatDisplayTime(timeString) {
      if (!timeString) return '';
      const normalized = timeString.replace('.', ':');
      const parsed = moment(normalized, ['HH:mm', 'HH:mm:ss', 'h:mm A'], true);
      return parsed.isValid() ? parsed.format('h:mm A') : timeString;
    },
    resolveTimezoneName(submissionValue, punchValue) {
      return submissionValue || punchValue || moment.tz.guess();
    },
    toFormDate(dateString) {
      if (!dateString) {
        return '';
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      const parsed = parseDate(dateString, this.userDateFormat);
      return parsed
        ? formatDate(parsed, 'yyyy-MM-dd', {locale: this.locale})
        : '';
    },
    toFormTime(timeString) {
      if (!timeString) {
        return '';
      }
      const normalized = timeString.replace('.', ':');
      const parsed = parseTime(normalized, this.timeFormat);
      return parsed
        ? formatTime(parsed, 'HH:mm', {locale: this.locale})
        : normalized;
    },
    getPunchDateTimeFromRecord(punch, timezoneName) {
      if (!punch) {
        return {date: '', time: ''};
      }
      if (punch.userDateYmd && punch.userTime) {
        return {
          date: punch.userDateYmd,
          time: this.toFormTime(punch.userTime),
        };
      }
      if (punch.utcDateTime && timezoneName) {
        const local = moment(punch.utcDateTime).tz(timezoneName);
        if (local.isValid()) {
          return {
            date: local.format('YYYY-MM-DD'),
            time: local.format('HH:mm'),
          };
        }
      }
      if (punch.utcDate && punch.utcTime) {
        const utcDateTime = moment.utc(
          `${punch.utcDate} ${punch.utcTime}`,
          ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss', 'DD-MM-YYYY HH:mm'],
          true,
        );
        if (utcDateTime.isValid()) {
          const local = utcDateTime.tz(timezoneName);
          return {
            date: local.format('YYYY-MM-DD'),
            time: local.format('HH:mm'),
          };
        }
      }
      return {
        date: this.toFormDate(punch.userDate),
        time: this.toFormTime(punch.userTime),
      };
    },
    getSubmissionDateTime(submission, fieldPrefix) {
      const userTime = submission?.[`${fieldPrefix}UserTime`];
      const timezoneName = submission?.[`${fieldPrefix}TimezoneName`];
      if (!userTime || !timezoneName) {
        return {date: '', time: ''};
      }
      return {
        date: this.convertUtcToLocalDate(userTime, timezoneName),
        time: this.convertUtcToLocalTime(userTime, timezoneName),
      };
    },
    buildEditAttendanceAdjustment(record) {
      const submission = record.submission;
      const punchInTimezone = this.resolveTimezoneName(
        submission?.punchInTimezoneName,
        record.punchIn?.timezoneName,
      );
      const punchOutTimezone = this.resolveTimezoneName(
        submission?.punchOutTimezoneName,
        record.punchOut?.timezoneName || punchInTimezone,
      );
      const submissionPunchIn = this.getSubmissionDateTime(
        submission,
        'punchIn',
      );
      const submissionPunchOut = this.getSubmissionDateTime(
        submission,
        'punchOut',
      );
      const recordPunchIn = this.getPunchDateTimeFromRecord(
        record.punchIn,
        punchInTimezone,
      );
      const recordPunchOut = this.getPunchDateTimeFromRecord(
        record.punchOut,
        punchOutTimezone,
      );

      return {
        attendanceId: this.attendanceId,
        note: submission?.note || '',
        punchInTimezoneName: this.getUtcOffsetLabel(punchInTimezone),
        punchInUserDate: submissionPunchIn.date || recordPunchIn.date,
        punchInUserTime: submissionPunchIn.time || recordPunchIn.time,
        punchInUserTimeOriginal: submission?.punchInUserTime || '',
        punchOutUserTimeOriginal: submission?.punchOutUserTime || '',
        punchOutTimezoneName: this.getUtcOffsetLabel(punchOutTimezone),
        punchOutUserDate: submissionPunchOut.date || recordPunchOut.date,
        punchOutUserTime: submissionPunchOut.time || recordPunchOut.time,
        submissionStatus: submission?.status || '',
        submissionId: submission?.id || 0,
        systemInOut: `${
          record.punchIn?.userDate || ''
        } (${this.formatDisplayTime(record.punchIn?.userTime)}) - ${
          record.punchOut?.userDate || ''
        } (${this.formatDisplayTime(record.punchOut?.userTime)})`,
      };
    },
    getUtcOffsetLabel(timezone) {
      const offset = moment.tz(timezone).format('Z');
      return {
        id: timezone,
        label: `(UTC${offset}) ${timezone}`,
      };
    },
    convertToUtcIso(dateString, timeString, timeZoneName) {
      if (!dateString || !timeString || !timeZoneName) return null;
      const localDateTime = `${dateString}T${timeString}`;

      // Convert to UTC ISO string
      return moment.tz(localDateTime, timeZoneName).utc().format();
    },
    convertToUserTime(dateString, timeString, timeZoneName) {
      if (!dateString || !timeString || !timeZoneName) return null;
      const localDateTime = `${dateString}T${timeString}`;
      return moment.tz(localDateTime, timeZoneName).format();
    },
    convertUtcToLocalTime(utcTime, timeZoneName) {
      if (!utcTime || !timeZoneName) return '';
      const local = moment(utcTime).tz(timeZoneName);
      return local.isValid() ? local.format('HH:mm') : '';
    },
    convertUtcToLocalDate(utcTime, timeZoneName) {
      if (!utcTime || !timeZoneName) return '';
      const local = moment(utcTime).tz(timeZoneName);
      return local.isValid() ? local.format('YYYY-MM-DD') : '';
    },
    assertNotFuture(dateString, timeString, timeZoneName) {
      if (!dateString || !timeString || !timeZoneName) {
        return true;
      }
      const localDateTime = `${dateString}T${timeString}`;
      return moment.tz(localDateTime, timeZoneName).isSameOrBefore(moment());
    },
    buildAdjustmentPayload() {
      if (!this.hasCompletePunchIn && !this.hasCompletePunchOut) {
        this.$toast.show({
          type: 'error',
          title: this.$t('general.error'),
          message: 'Provide clock in or clock out date and time',
        });
        return null;
      }

      if (
        this.hasCompletePunchIn &&
        !this.assertNotFuture(
          this.editAttendanceAdjustment.punchInUserDate,
          this.editAttendanceAdjustment.punchInUserTime,
          this.editAttendanceAdjustment.punchInTimezoneName?.id,
        )
      ) {
        this.$toast.show({
          type: 'error',
          title: this.$t('general.error'),
          message: 'Clock in date and time cannot be in the future',
        });
        return null;
      }

      if (
        this.hasCompletePunchOut &&
        !this.assertNotFuture(
          this.editAttendanceAdjustment.punchOutUserDate,
          this.editAttendanceAdjustment.punchOutUserTime,
          this.editAttendanceAdjustment.punchOutTimezoneName?.id,
        )
      ) {
        this.$toast.show({
          type: 'error',
          title: this.$t('general.error'),
          message: 'Clock out date and time cannot be in the future',
        });
        return null;
      }

      const payload = {
        note: this.editAttendanceAdjustment.note,
      };

      if (this.hasCompletePunchIn) {
        payload.punchInUserTime = this.convertToUserTime(
          this.editAttendanceAdjustment.punchInUserDate,
          this.editAttendanceAdjustment.punchInUserTime,
          this.editAttendanceAdjustment.punchInTimezoneName.id,
        );
        payload.punchInTimezoneName =
          this.editAttendanceAdjustment.punchInTimezoneName.id;
      }

      if (this.hasCompletePunchOut) {
        payload.punchOutUserTime = this.convertToUserTime(
          this.editAttendanceAdjustment.punchOutUserDate,
          this.editAttendanceAdjustment.punchOutUserTime,
          this.editAttendanceAdjustment.punchOutTimezoneName.id,
        );
        payload.punchOutTimezoneName =
          this.editAttendanceAdjustment.punchOutTimezoneName.id;
      }

      return payload;
    },
    isPendingSubmission(status) {
      return Number(status) === PENDING_SUBMISSION_STATUS;
    },
    getPendingSubmissionId() {
      const {submissionId, submissionStatus} = this.editAttendanceAdjustment;
      if (this.isPendingSubmission(submissionStatus) && submissionId) {
        return submissionId;
      }
      return null;
    },
    async fetchPendingSubmissionId() {
      const fromForm = this.getPendingSubmissionId();
      if (fromForm) {
        return fromForm;
      }

      try {
        const response = await this.httpGet.get(this.attendanceId);
        const submission = response?.data?.data?.submission;
        if (this.isPendingSubmission(submission?.status) && submission?.id) {
          return submission.id;
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }

      return null;
    },
    isDuplicateSubmissionError(error) {
      return isDuplicateAdjustmentSubmissionError(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message,
      );
    },
    getApiErrorMessage(error) {
      return (
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        null
      );
    },
    submitUpdate(submissionId, payload) {
      return this.httpPut.update(submissionId, {
        employeeAttendanceId: this.editAttendanceAdjustment.attendanceId,
        ...payload,
        submissionStatus: PENDING_SUBMISSION_STATUS,
      });
    },
    async onSave() {
      const payload = this.buildAdjustmentPayload();
      if (!payload) {
        return;
      }

      this.isLoading = true;
      try {
        let pendingSubmissionId = await this.fetchPendingSubmissionId();
        if (pendingSubmissionId) {
          const confirmation =
            await this.$refs.pendingAdjustmentDialog.showDialog();
          if (confirmation !== 'ok') {
            return;
          }
        } else {
          try {
            await this.httpPut.create({
              employeeAttendanceId: this.editAttendanceAdjustment.attendanceId,
              ...payload,
            });
            await this.$toast.updateSuccess();
            this.onCancel();
            return;
          } catch (error) {
            if (!this.isDuplicateSubmissionError(error)) {
              this.$toast.unexpectedError(this.getApiErrorMessage(error));
              return;
            }

            pendingSubmissionId = await this.fetchPendingSubmissionId();
            if (!pendingSubmissionId) {
              this.$toast.unexpectedError(this.getApiErrorMessage(error));
              return;
            }

            const confirmation =
              await this.$refs.pendingAdjustmentDialog.showDialog();
            if (confirmation !== 'ok') {
              return;
            }
          }
        }

        await this.submitUpdate(pendingSubmissionId, payload);
        await this.$toast.updateSuccess();
        this.onCancel();
      } catch (error) {
        this.$toast.unexpectedError(this.getApiErrorMessage(error));
      } finally {
        this.isLoading = false;
      }
    },
    onCancel() {
      navigate('/attendance/viewMyAttendanceRecord');
    },
  },
};
</script>
